import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  createApi,
  FetchBaseQueryError,
  type TypedUseQueryHookResult,
  type TypedUseQuerySubscriptionOptions,
} from '@reduxjs/toolkit/query/react';
import { Role } from '@/models/AccountEnums';
import {
  AccountBase,
  AccountHistoricalComparisonItem,
  AccountHistoricalComparisonResponse,
  AccountHistoricalTrendResponse,
  AccountViewModel,
} from '../models/AccountViewModel';
import { RecordResponse } from '../models/ServiceTypes';
import {
  coerceFiniteNumber,
  extractListFromPayload,
  extractRecordsList,
  isRecord,
} from '@/utils/apiPayload';
import { toApiDateParam } from '@/utils/date';
import {
  advisorRequiresInvestorId,
  advisorScopeMissingError,
} from '@/utils/advisorScoping';
import {
  mergeViewingTagIntoArg,
  type ViewingTagSelection,
} from '@/utils/viewingTagSelection';
import {
  confirmedViewingTagSelection,
  selectQueryViewingTag,
} from './selectQueryViewingTag';
import { waServicesQuery } from './baseQueries';
import { WARootState } from './store';
import type { OfflineCreationPayload } from '@/modules/portfolio/accountModal/offlineAccountPayload';

export interface HistoricalValue {
  id?: number;
  FromDate: string;
  ToDate: string;
  Value: number;
}

export type OfflineAccount = AccountBase & {
  WealthId?: number;
  TopClassificationId?: number;
  MonthlyPaymentAmount?: number;
  InitialLoanAmount?: number;
  InterestRate?: number;
  LoanBalance?: number;
  Payment?: number;
  PropertyValue?: number;
  Address?: string;
  CityStateZip?: string;
  Zpid?: number;
  ZillowBalance?: number;
  HistoricalValues?: HistoricalValue[];
  DidHistoricalChange?: boolean;
};

export interface AccountTag {
  Name: string;
  InvestorTagId?: number;
  AccountTagId?: number;
  InvestorId?: number;
  ExternalId?: string;
}

/** Arguments for `getAccounts` / `useGetAccountsQuery` (tag filter merged automatically unless overridden). */
export interface GetAccountsQueryArg {
  showClosed?: boolean;
  asOfDate?: string;
  showHome?: boolean;
  showAway?: boolean;
  ExcludedAccounts?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedCategories?: number[];
  ExcludedInstitutions?: number[];
  ExcludedTags?: number[];
  ExcludedGroups?: { GroupId: string; ExcludedIds: string[] }[];
  InvestorId?: number;
  GroupId?: number;
  WidgetCode?: string;
  /**
   * Same numeric id as `UserTag.tagId` from `useGetUserTagsQuery` / GET `/api/user/tags`.
   * Sent as the **`InvestorTagId`** query parameter on `GET .../Accounts/`.
   */
  InvestorTagId?: number;
  TagId?: number;
  OnlyPrimaryInvestors?: boolean;
  sort?: {
    property: string;
    direction: 'asc' | 'desc';
    order: number;
  }[];
  group?: {
    ValueProperty: string;
    DisplayProperty: string;
    SortProperty?: string;
  };
  /**
   * Never sent. A retry raises it to give the request its own cache entry, because
   * a refetch while the original request still hangs only re-attaches to it.
   */
  retryAttempt?: number;
}

export interface GetVaultFileAccountsQueryArg {
  vaultFileCategoryId: number | string;
  advisorInvestorId?: number | string;
  /** Firm tags use `InvestorTagId`; investor-created tags use `TagId`. */
  InvestorTagId?: number;
  TagId?: number;
}

/**
 * Merges the global viewing-tag selection into getAccounts args.
 * Skips when the caller already set `InvestorTagId`, or when `InvestorId` is set (advisor/client-scoped fetch).
 */
export function mergeViewingInvestorTagIntoGetAccountsArg(
  arg: GetAccountsQueryArg,
  selection: ViewingTagSelection,
): GetAccountsQueryArg {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

/** Arguments for portfolio Accounts Historical Values comparison. */
export interface AccountHistoricalComparisonQueryArg {
  startDate: string;
  endDate: string;
  groupConfig?: {
    valueProperty: string;
    displayProperty: string;
  };
  showAll?: boolean;
  sort?: Array<{
    property: string;
    direction: 'asc' | 'desc';
    order: number;
  }>;
  accountId?: number;
  ExcludedAccounts?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedCategories?: number[];
  ExcludedInstitutions?: number[];
  InvestorId?: number;
  /**
   * Same numeric id as `UserTag.tagId` from `useGetUserTagsQuery`.
   * Sent as the **`InvestorTagId`** query parameter (System A).
   */
  InvestorTagId?: number;
  TagId?: number;
}

/**
 * Merges the global viewing-tag selection into account historical comparison args.
 * Skips when the caller already set `InvestorTagId`, or when `InvestorId` is set
 * (advisor/client-scoped fetch) — same rules as `mergeViewingInvestorTagIntoGetAccountsArg`.
 */
export function mergeViewingInvestorTagIntoAccountHistoricalComparisonArg(
  arg: AccountHistoricalComparisonQueryArg,
  selection: ViewingTagSelection,
): AccountHistoricalComparisonQueryArg {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

/**
 * Spread into `useGetAccountsQuery` args when the UI needs every account (e.g. filter pickers),
 * regardless of the tag selected in the header.
 */
export const skipViewingInvestorTagForAccountsQuery: Partial<GetAccountsQueryArg> =
  {
    InvestorTagId: undefined,
  };

export function mergeViewingInvestorTagIntoVaultFileAccountsArg(
  arg: GetVaultFileAccountsQueryArg,
  selection: ViewingTagSelection,
): GetVaultFileAccountsQueryArg {
  if ('advisorInvestorId' in arg && arg.advisorInvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export function buildVaultFileAccountsSubscriptionArg(
  arg: GetVaultFileAccountsQueryArg,
  selection: ViewingTagSelection,
): GetVaultFileAccountsQueryArg {
  return mergeViewingInvestorTagIntoVaultFileAccountsArg(arg, selection);
}

function isAccountTagRow(row: unknown): row is AccountTag {
  return isRecord(row) && typeof row.Name === 'string';
}

function normalizeAccountTagsResponse(data: unknown): AccountTag[] {
  return extractRecordsList(data).filter(isAccountTagRow);
}

function readNumericField(
  row: Record<string, unknown>,
  pascalKey: string,
  camelKey: string,
): number | null {
  return (
    coerceFiniteNumber(row[pascalKey]) ?? coerceFiniteNumber(row[camelKey])
  );
}

function normalizeAccountHistoricalComparisonItem(
  row: unknown,
): AccountHistoricalComparisonItem {
  const record = isRecord(row) ? row : {};
  const item = row as AccountHistoricalComparisonItem;

  return {
    ...item,
    OldDollarBalance: readNumericField(
      record,
      'OldDollarBalance',
      'oldDollarBalance',
    ),
    DollarBalance: readNumericField(record, 'DollarBalance', 'dollarBalance'),
    DollarChange: readNumericField(record, 'DollarChange', 'dollarChange'),
    PercentChange: readNumericField(record, 'PercentChange', 'percentChange'),
  };
}

function normalizeAccountHistoricalComparisonResponse(
  data: unknown,
): AccountHistoricalComparisonResponse {
  const records = extractListFromPayload(data).map(
    normalizeAccountHistoricalComparisonItem,
  );
  const totalLength =
    isRecord(data) && typeof data.TotalLength === 'number'
      ? data.TotalLength
      : records.length;

  return { Records: records, TotalLength: totalLength };
}

export interface VaultFileAccount {
  WealthAccountId: number;
  Name: string;
  AccountNumber: string;
}

export interface AccountGroupCategory {
  TagCategoryId: number;
  Name: string;
  IsAdvisorTag: boolean;
  IsEnabled: boolean;
  Description?: string;
  Tags: { Name: string; InvestorTagId: number }[];
}

interface AccountMarketPriceRecord {
  InvestorId: number;
  HoldingId: number;
  Name: string;
  AccountName: string;
  AccountId: string;
  Balance: number;
  Quantity: number;
  Price: number;
  PriceDollarChange: number;
  PricePercentChange: number;
  BalanceChange: number;
  LastUpdated: string;
  InstitutionName: string;
  WealthAccountId: number;
}

type AccountMarketPriceResponse = RecordResponse<AccountMarketPriceRecord>;

export interface GetAccountMarketPriceQueryArg {
  groupConfig: { valueProperty: string; displayProperty: string };
  asOfDate?: string;
  sort?: Array<{
    property: string;
    direction: 'asc' | 'desc';
    order: number;
  }>;
  _?: string;
  ExcludedAccounts?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedCategories?: number[];
  ExcludedInstitutions?: number[];
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
}

export function mergeViewingInvestorTagIntoMarketPriceArg(
  arg: GetAccountMarketPriceQueryArg,
  selection: ViewingTagSelection,
): GetAccountMarketPriceQueryArg {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export function buildMarketPriceSubscriptionArg(
  arg: GetAccountMarketPriceQueryArg,
  selection: ViewingTagSelection,
): GetAccountMarketPriceQueryArg {
  return mergeViewingInvestorTagIntoMarketPriceArg(arg, selection);
}

export interface AccountUnrealizedGainsRecord {
  WealthHoldingId: number;
  WealthAccountId: number;
  WealthId: number;
  HoldingName: string;
  AccountName: string;
  WealthName: string;
  Symbol?: string;
  CUSIP?: string;
  NumberOfTaxLots: number;
  Quantity: number;
  Price: number;
  CostBasis: number;
  CurrentBalance: number;
  DollarChange: number;
  PercentChange: number;
  OpenDate: string;
  TaxLotTerm: number;
  TaxLotTermDisplay: string;
  GainType: number;
  GainTypeDisplay: string;
}

export interface AccountRealizedGainsRecord {
  WealthHoldingId: number;
  WealthAccountId: number;
  WealthId: number;
  HoldingName: string;
  AccountName: string;
  WealthName: string;
  Symbol?: string;
  CUSIP?: string;
  NumberOfTaxLots: number;
  Quantity: number;
  Price: number;
  CostBasis: number;
  SalePrice: number;
  DollarChange: number;
  PercentChange: number;
  TradeDate?: string;
  SaleDate?: string;
  OpenDate?: string;
  SaleType?: string;
  GainType: number;
  GainTypeDisplay: string;
  TaxLotTerm: number;
  TaxLotTermDisplay: string;
}

/** RTK Query placeholder when an endpoint takes no arguments. */
type QueryNoArgs = Record<string, never>;

function sortParamKey(
  index: number,
  field: 'Property' | 'Direction' | 'Order',
): string {
  return `sort[${String(index)}].${field}`;
}

function roleApiPath(role: string | undefined, path: string): string {
  return `/api/${role ?? ''}${path}`;
}

interface LegacyHistoricalTrendPoint {
  date?: string;
  Date?: string;
  value?: number;
  Value?: number;
}

function isLegacyHistoricalTrendPayload(
  value: unknown,
): value is { data: LegacyHistoricalTrendPoint[] } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    Array.isArray(value.data)
  );
}

/**
 * API service for account-related endpoints
 */
export const accountsApi = createApi({
  reducerPath: 'accountsApi',
  baseQuery: waServicesQuery,
  keepUnusedDataFor: 600,
  tagTypes: [
    'Accounts',
    'AccountsHistorical',
    'AccountTags',
    'AccountGroupCategories',
    'AccountGroupDefaults',
    'Holdings',
    'Investors',
    'Portfolio',
    'Tags',
    'Wealths',
  ],

  endpoints: (builder) => ({
    // Advisor Investor Groups - matches legacy Advisor/InvestorGroups signature
    getAdvisorInvestorGroups: builder.query<
      RecordResponse<{
        AdvisorInvestorGroupId?: number;
        AdvisorNamedInvestorGroupId?: number;
        Name: string;
        GroupName?: string;
        ParentId?: number;
      }>,
      {
        limit?: number;
        sort?: { property: string; direction: 'asc' | 'desc' }[];
      }
    >({
      queryFn: async (
        { limit = 100, sort = [{ property: 'Name', direction: 'asc' }] },
        _ctx,
        _unused,
        baseQuery,
      ) => {
        const params: Record<string, string | number | boolean> = { limit };
        sort.forEach((s, i) => {
          params[sortParamKey(i, 'Property')] = s.property;
          params[sortParamKey(i, 'Direction')] = s.direction;
        });
        const result = await baseQuery({
          url: `/api/Advisor/InvestorGroups/`,
          method: 'GET',
          params,
        });
        if (result.error) {
          return { error: result.error };
        }
        const raw = result.data as
          | RecordResponse<{
              AdvisorInvestorGroupId?: number;
              AdvisorNamedInvestorGroupId?: number;
              Name: string;
              GroupName?: string;
              ParentId?: number;
            }>
          | {
              AdvisorInvestorGroupId?: number;
              AdvisorNamedInvestorGroupId?: number;
              Name: string;
              GroupName?: string;
              ParentId?: number;
            }[];
        if (Array.isArray(raw)) {
          return {
            data: {
              Records: raw,
              TotalLength: raw.length,
            } as RecordResponse<{
              AdvisorInvestorGroupId?: number;
              AdvisorNamedInvestorGroupId?: number;
              Name: string;
              GroupName?: string;
              ParentId?: number;
            }>,
          };
        }
        return {
          data: raw,
        };
      },
      providesTags: [{ type: 'Accounts', id: 'InvestorGroups' }],
    }),
    // Resolve a single group by id — used to display a deep-linked `?groupId=`
    // label on page load, when the group isn't in the lazily-loaded options.
    // Backend fetches all groups then filters, so any id resolves (even beyond
    // the client-side 100-record fetch cap).
    getAdvisorInvestorGroupById: builder.query<
      {
        AdvisorInvestorGroupId?: number;
        AdvisorNamedInvestorGroupId?: number;
        Name: string;
        GroupName?: string;
        ParentId?: number;
      },
      number
    >({
      queryFn: async (groupId, _ctx, _unused, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/InvestorGroups/${String(groupId)}`,
          method: 'GET',
        });
        if (result.error) {
          return { error: result.error };
        }
        return {
          data: result.data as {
            AdvisorInvestorGroupId?: number;
            AdvisorNamedInvestorGroupId?: number;
            Name: string;
            GroupName?: string;
            ParentId?: number;
          },
        };
      },
      providesTags: [{ type: 'Accounts', id: 'InvestorGroups' }],
    }),
    getAccounts: builder.query<AccountViewModel, GetAccountsQueryArg>({
      queryFn: async (
        {
          showClosed,
          asOfDate,
          showHome,
          showAway,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          ExcludedInstitutions,
          ExcludedTags,
          ExcludedGroups,
          InvestorId,
          GroupId,
          InvestorTagId,
          TagId,
          OnlyPrimaryInvestors,
          WidgetCode,
          sort,
          group,
        },
        { getState },
        _unused,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // Household / viewing filter: send **`InvestorTagId` only** (same value as `UserTag.tagId`
        // from `useGetUserTagsQuery`); do not send `TagId` or other aliases.
        const params: Record<string, string | number | boolean> = {
          ...(typeof showClosed !== 'undefined' ? { showClosed } : {}),
          ...(typeof asOfDate !== 'undefined' ? { asOfDate } : {}),
          ...(typeof showHome !== 'undefined' ? { ShowHome: showHome } : {}),
          ...(typeof showAway !== 'undefined' ? { ShowAway: showAway } : {}),
          ...(typeof GroupId !== 'undefined' ? { GroupId } : {}),
          ...(typeof InvestorId !== 'undefined' ? { InvestorId } : {}),
          ...(typeof InvestorTagId === 'number' &&
          Number.isFinite(InvestorTagId)
            ? { InvestorTagId }
            : {}),
          ...(typeof TagId === 'number' && Number.isFinite(TagId)
            ? { TagId }
            : {}),
          ...(typeof OnlyPrimaryInvestors !== 'undefined'
            ? { OnlyPrimaryInvestors }
            : {}),
          ...(typeof WidgetCode !== 'undefined' ? { WidgetCode } : {}),
          ...(ExcludedAccounts && ExcludedAccounts.length
            ? { ExcludedAccounts: ExcludedAccounts.join(',') }
            : {}),
          ...(ExcludedAccountTypes && ExcludedAccountTypes.length
            ? { ExcludedAccountTypes: ExcludedAccountTypes.join(',') }
            : {}),
          ...(ExcludedCategories && ExcludedCategories.length
            ? { ExcludedCategories: ExcludedCategories.join(',') }
            : {}),
          ...(ExcludedInstitutions && ExcludedInstitutions.length
            ? { ExcludedInstitutions: ExcludedInstitutions.join(',') }
            : {}),
          ...(ExcludedTags && ExcludedTags.length
            ? { ExcludedTags: ExcludedTags.join(',') }
            : {}),
          ...(ExcludedGroups && ExcludedGroups.length > 0
            ? { ExcludedGroups: JSON.stringify(ExcludedGroups) }
            : {}),
          ...(group
            ? {
                'group.ValueProperty': group.ValueProperty,
                'group.DisplayProperty': group.DisplayProperty,
                ...(group.SortProperty
                  ? { 'group.SortProperty': group.SortProperty }
                  : {}),
              }
            : {}),
        };

        if (sort && sort.length > 0) {
          sort.forEach((s, i) => {
            params[sortParamKey(i, 'Property')] = s.property;
            params[sortParamKey(i, 'Direction')] = s.direction;
            params[sortParamKey(i, 'Order')] = s.order;
          });
        }

        const result = await baseQuery({
          url: roleApiPath(currentRole, '/Accounts/'),
          params,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as AccountViewModel };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.Records.map(({ AccountId }) => ({
                type: 'Accounts' as const,
                id: AccountId,
              })),
              { type: 'Accounts', id: 'LIST' },
            ]
          : [{ type: 'Accounts', id: 'LIST' }],
    }),
    getAdvisorAccount: builder.query<AccountBase, number>({
      query: (accountId) => ({
        url: `/api/advisor/accounts/${String(accountId)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, accountId) => [
        { type: 'Accounts', id: accountId },
      ],
    }),
    uploadAccountHistory: builder.mutation<
      unknown,
      {
        accountId: number | string;
        file: File;
        filename?: string;
        investorId?: number;
      }
    >({
      queryFn: async (
        { accountId, file, filename, investorId },
        { getState },
        _unused,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole ?? Role.Investor;
        if (advisorRequiresInvestorId(currentRole, investorId)) {
          return { error: advisorScopeMissingError() };
        }

        const formData = new FormData();
        const nameToUse = filename || file.name;
        formData.append('filename', nameToUse);
        formData.append('file', file, nameToUse);

        const result = await baseQuery({
          url: roleApiPath(
            currentRole,
            `/Accounts/${String(accountId)}/uploadAccountHistory/`,
          ),
          method: 'POST',
          body: formData,
          ...(typeof investorId === 'number' ? { params: { investorId } } : {}),
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data };
      },
      invalidatesTags: ['AccountsHistorical'],
    }),

    // Role-aware Account Bar Charts for Balance Sheet tab in client flyout
    getAccountBarCharts: builder.query<
      unknown,
      {
        investorId: number;
        groupingProperty: {
          valueProperty: string;
          displayProperty: string;
          sortProperty?: string;
        };
        _?: string;
      }
    >({
      queryFn: async (
        { investorId, groupingProperty, _ },
        { getState },
        _unused,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const params: Record<string, string | number | boolean> = {
          InvestorId: investorId,
          'groupingProperty.valueProperty': groupingProperty.valueProperty,
          'groupingProperty.displayProperty': groupingProperty.displayProperty,
        };

        if (groupingProperty.sortProperty) {
          params['groupingProperty.sortProperty'] =
            groupingProperty.sortProperty;
        }

        params._ = _ || Date.now().toString();

        const result = await baseQuery({
          url: roleApiPath(currentRole, '/AccountBarCharts'),
          method: 'GET',
          params,
        });

        if (result.error) return { error: result.error };
        return { data: result.data };
      },
      providesTags: ['AccountsHistorical'],
    }),

    getAccountsHistorical: builder.query<
      AccountHistoricalTrendResponse | null,
      | {
          startDate: string;
          endDate: string;
          trendType: string;
          groupConfig?: {
            valueProperty: string;
            displayProperty: string;
          };
          accountId?: number;
          _?: string; // Add timestamp parameter for cache busting
          ExcludedAccounts?: number[];
          ExcludedAccountTypes?: number[];
          ExcludedCategories?: number[];
          ExcludedInstitutions?: number[];
          InvestorTagId?: number;
        }
      | typeof skipToken
    >({
      queryFn: async (args, { getState }, _unused, baseQuery) => {
        if (args === skipToken) return { data: null };

        const {
          startDate,
          endDate,
          trendType,
          groupConfig,
          accountId,
          _,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          ExcludedInstitutions,
          InvestorTagId,
        } = args;
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // Build the URL based on whether we're querying for a specific account or all accounts
        const url = accountId
          ? roleApiPath(
              currentRole,
              `/AccountHistoricalTrend/${String(accountId)}`,
            )
          : roleApiPath(currentRole, '/AccountHistoricalTrend');

        // Format parameters to match the expected API format as shown in the example
        const params: Record<string, string | number | boolean> = {
          startDate,
          endDate,
          trendType,
          ...(groupConfig
            ? {
                'groupConfig.valueProperty': groupConfig.valueProperty,
                'groupConfig.displayProperty': groupConfig.displayProperty,
              }
            : {}),
          ...(ExcludedAccounts?.length
            ? { ExcludedAccounts: ExcludedAccounts.join(',') }
            : {}),
          ...(ExcludedAccountTypes?.length
            ? { ExcludedAccountTypes: ExcludedAccountTypes.join(',') }
            : {}),
          ...(ExcludedCategories?.length
            ? { ExcludedCategories: ExcludedCategories.join(',') }
            : {}),
          ...(ExcludedInstitutions?.length
            ? { ExcludedInstitutions: ExcludedInstitutions.join(',') }
            : {}),
          ...(typeof InvestorTagId === 'number' &&
          Number.isFinite(InvestorTagId)
            ? { InvestorTagId }
            : {}),
        };

        // Add timestamp parameter if provided
        if (_) {
          params._ = _;
        } else {
          // Add current timestamp if not provided
          params._ = Date.now().toString();
        }

        try {
          const result = await baseQuery({
            url,
            method: 'GET',
            params,
          });

          if (result.error) {
            return { error: result.error };
          }

          if (!result.data) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: 'No data returned from API' },
                error: 'No data returned from API',
              } as FetchBaseQueryError,
            };
          }

          // Check if data matches expected structure
          const responseData = result.data as AccountHistoricalTrendResponse;
          if (!Array.isArray(responseData.Records)) {
            // Try to transform any data we received into the expected format
            if (Array.isArray(responseData)) {
              const transformed = {
                Records: responseData,
                TotalLength: responseData.length,
                SummedValue: null,
              };
              return { data: transformed };
            }

            if (isLegacyHistoricalTrendPayload(responseData)) {
              const transformed = {
                Records: [
                  {
                    name: 'Historical Trend',
                    id: 1,
                    data: responseData.data.map((item) => ({
                      Date: item.date ?? item.Date ?? '',
                      Value: item.value ?? item.Value ?? 0,
                    })),
                  },
                ],
                TotalLength: 1,
                SummedValue: null,
              };
              return { data: transformed };
            }
          } else {
            console.log(
              'HISTORICAL TRENDS API SUCCESS. Records:',
              responseData.Records.length,
            );
          }

          return { data: responseData };
        } catch (err) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: ['AccountsHistorical'],
    }),
    getAccountHistoricalComparison: builder.query<
      AccountHistoricalComparisonResponse | null,
      AccountHistoricalComparisonQueryArg | typeof skipToken
    >({
      queryFn: async (args, { getState }, _unused, baseQuery) => {
        if (args === skipToken) return { data: null };

        const {
          startDate,
          endDate,
          groupConfig,
          showAll,
          sort,
          accountId,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          ExcludedInstitutions,
          InvestorId,
          InvestorTagId,
          TagId,
        } = args;
        const currentRole = (getState() as WARootState).auth.currentRole;
        const url = roleApiPath(currentRole, '/accountHistoricalComparison/');

        // Dynamically build params. Viewing-tag filter is merged by
        // `useGetAccountHistoricalComparisonQuery` (same as `useGetAccountsQuery`).
        const params: Record<string, string | number | boolean> = {
          startDate: toApiDateParam(startDate),
          endDate: toApiDateParam(endDate),
        };

        if (accountId) {
          params.accountId = accountId;
        }

        if (ExcludedAccounts?.length) {
          params.ExcludedAccounts = ExcludedAccounts.join(',');
        }

        if (ExcludedAccountTypes?.length) {
          params.ExcludedAccountTypes = ExcludedAccountTypes.join(',');
        }

        if (ExcludedCategories?.length) {
          params.ExcludedCategories = ExcludedCategories.join(',');
        }

        if (ExcludedInstitutions?.length) {
          params.ExcludedInstitutions = ExcludedInstitutions.join(',');
        }

        if (typeof InvestorId !== 'undefined') {
          params.InvestorId = InvestorId;
        }

        // Household / viewing filter: send **`InvestorTagId` only** (same as getAccounts).
        if (
          typeof InvestorTagId === 'number' &&
          Number.isFinite(InvestorTagId)
        ) {
          params.InvestorTagId = InvestorTagId;
        }

        if (typeof TagId === 'number' && Number.isFinite(TagId)) {
          params.TagId = TagId;
        }

        if (groupConfig) {
          params['groupConfig.valueProperty'] = groupConfig.valueProperty;
          params['groupConfig.displayProperty'] = groupConfig.displayProperty;
        }

        if (showAll !== undefined) {
          params.showAll = showAll;
        }

        if (sort && sort.length > 0) {
          sort.forEach((sortItem, index) => {
            params[sortParamKey(index, 'Property')] = sortItem.property;
            params[sortParamKey(index, 'Direction')] = sortItem.direction;
            params[sortParamKey(index, 'Order')] = sortItem.order;
          });
        }

        const result = await baseQuery({
          url,
          method: 'GET',
          params,
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: normalizeAccountHistoricalComparisonResponse(result.data),
        };
      },
      providesTags: ['AccountsHistorical'],
    }),
    getAccountHistoricalTrendDetails: builder.query<
      unknown,
      {
        selectedDate: string;
        comparisonDate: string;
        trendType: string;
        ExcludedAccounts?: number[];
        ExcludedAccountTypes?: number[];
        ExcludedCategories?: number[];
        ExcludedInstitutions?: number[];
      }
    >({
      queryFn: async (
        {
          selectedDate,
          comparisonDate,
          trendType,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          ExcludedInstitutions,
        },
        { getState },
        _unused,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: roleApiPath(currentRole, '/AccountHistoricalTrendDetails'),
          method: 'GET',
          params: {
            selectedDate,
            comparisonDate,
            trendType,
            ...(ExcludedAccounts && ExcludedAccounts.length
              ? { ExcludedAccounts: ExcludedAccounts.join(',') }
              : {}),
            ...(ExcludedAccountTypes && ExcludedAccountTypes.length
              ? { ExcludedAccountTypes: ExcludedAccountTypes.join(',') }
              : {}),
            ...(ExcludedCategories && ExcludedCategories.length
              ? { ExcludedCategories: ExcludedCategories.join(',') }
              : {}),
            ...(ExcludedInstitutions && ExcludedInstitutions.length
              ? { ExcludedInstitutions: ExcludedInstitutions.join(',') }
              : {}),
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data };
      },
    }),
    getAccountMarketPrice: builder.query<
      AccountMarketPriceResponse,
      GetAccountMarketPriceQueryArg
    >({
      queryFn: async (args, { getState }, _unused, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const {
          groupConfig,
          asOfDate,
          sort,
          _,
          ExcludedAccounts,
          ExcludedAccountTypes,
          ExcludedCategories,
          ExcludedInstitutions,
          InvestorId,
          InvestorTagId,
          TagId,
        } = mergeViewingInvestorTagIntoMarketPriceArg(
          args,
          confirmedViewingTagSelection(state),
        );

        const params: Record<string, string | number | boolean> = {
          'Group.ValueProperty': groupConfig.valueProperty,
          'Group.DisplayProperty': groupConfig.displayProperty,
        };

        if (typeof asOfDate !== 'undefined') {
          params.asOfDate = asOfDate;
        }
        if (ExcludedAccounts?.length) {
          params.ExcludedAccounts = ExcludedAccounts.join(',');
        }
        if (ExcludedAccountTypes?.length) {
          params.ExcludedAccountTypes = ExcludedAccountTypes.join(',');
        }
        if (ExcludedCategories?.length) {
          params.ExcludedCategories = ExcludedCategories.join(',');
        }
        if (ExcludedInstitutions?.length) {
          params.ExcludedInstitutions = ExcludedInstitutions.join(',');
        }
        if (typeof InvestorId !== 'undefined') {
          params.InvestorId = InvestorId;
        }
        if (
          typeof InvestorTagId === 'number' &&
          Number.isFinite(InvestorTagId)
        ) {
          params.InvestorTagId = InvestorTagId;
        }
        if (typeof TagId === 'number' && Number.isFinite(TagId)) {
          params.TagId = TagId;
        }

        // Add timestamp parameter if provided, otherwise use current timestamp
        params._ = _ || Date.now().toString();

        // Handle sort parameters
        if (sort && sort.length > 0) {
          sort.forEach((sortItem, index) => {
            params[sortParamKey(index, 'Property')] = sortItem.property;
            params[sortParamKey(index, 'Direction')] = sortItem.direction;
            params[sortParamKey(index, 'Order')] = sortItem.order;
          });
        }

        const result = await baseQuery({
          url: roleApiPath(currentRole, '/marketpricing'),
          method: 'GET',
          params,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as AccountMarketPriceResponse };
      },
      providesTags: ['Portfolio'],
    }),
    getVaultFileAccounts: builder.query<
      VaultFileAccount[], // Updated to use specific type
      GetVaultFileAccountsQueryArg
    >({
      queryFn: async (
        { vaultFileCategoryId, advisorInvestorId, InvestorTagId, TagId },
        _queryApi,
        _unused,
        baseQuery,
      ) => {
        // Determine the advisorInvestorId segment for the URL, defaulting to empty string if not provided
        const advisorIdSegment =
          advisorInvestorId != null ? String(advisorInvestorId) : '';
        const url = `/api/investor/Documents2/Accounts/${String(vaultFileCategoryId)}/${advisorIdSegment}`;
        const tagParams: Record<string, number> = {};
        if (
          typeof InvestorTagId === 'number' &&
          Number.isFinite(InvestorTagId)
        ) {
          tagParams.InvestorTagId = InvestorTagId;
        }
        if (typeof TagId === 'number' && Number.isFinite(TagId)) {
          tagParams.TagId = TagId;
        }

        const result = await baseQuery({
          url: url,
          method: 'GET',
          params: Object.keys(tagParams).length > 0 ? tagParams : undefined,
        });

        if (result.error) {
          // Consider more specific error handling if needed
          return { error: result.error };
        }

        // Return the typed response
        return { data: result.data as VaultFileAccount[] };
      },
      providesTags: ['Accounts'], // Assuming this relates to accounts data
    }),
    getAccountHistoricalTrend: builder.query<
      unknown,
      { accountId: string; startDate: string; endDate: string }
    >({
      queryFn: async (
        { accountId, startDate, endDate },
        _ctx,
        _unused,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `/api/investor/AccountHistoricalTrend/${accountId}`,
          params: { startDate, endDate, trendType: 'trendForAccounts' },
        });
        return result.data ? { data: result.data } : { error: result.error };
      },
    }),
    getSingleAccountHistoricalComparison: builder.query<
      unknown,
      { accountId: string; startDate: string; endDate: string }
    >({
      queryFn: async (
        { accountId, startDate, endDate },
        _ctx,
        _unused,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `/api/investor/accountHistoricalComparison/`,
          params: {
            accountId,
            startDate: toApiDateParam(startDate),
            endDate: toApiDateParam(endDate),
            showAll: true,
          },
        });
        return result.data ? { data: result.data } : { error: result.error };
      },
    }),
    getAccountUnrealizedGains: builder.query<
      RecordResponse<AccountUnrealizedGainsRecord>,
      string
    >({
      queryFn: async (accountId, _ctx, _unused, baseQuery) => {
        const result = await baseQuery(
          `/api/investor/Accounts/${accountId}/unrealizedGains`,
        );

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: result.data as RecordResponse<AccountUnrealizedGainsRecord>,
        };
      },
    }),
    getAccountRealizedGains: builder.query<
      RecordResponse<AccountRealizedGainsRecord>,
      string
    >({
      queryFn: async (accountId, _ctx, _unused, baseQuery) => {
        const result = await baseQuery(
          `/api/investor/Accounts/${accountId}/realizedGains`,
        );

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: result.data as RecordResponse<AccountRealizedGainsRecord>,
        };
      },
    }),
    getAccountRealizedGainsSummary: builder.query<unknown, string>({
      queryFn: async (accountId, _ctx, _unused, baseQuery) => {
        const result = await baseQuery(
          `/api/investor/Accounts/${accountId}/realizedGainsSummary`,
        );
        return result.data ? { data: result.data } : { error: result.error };
      },
    }),
    getAccountDocuments: builder.query<unknown, string>({
      queryFn: async (accountId, _ctx, _unused, baseQuery) => {
        const result = await baseQuery(
          `/api/investor/Accounts/${accountId}/documents`,
        );
        return result.data ? { data: result.data } : { error: result.error };
      },
    }),
    getAccountTransactions: builder.query<
      unknown,
      { accountId: string; startDate: string; endDate: string }
    >({
      queryFn: async (
        { accountId, startDate, endDate },
        _ctx,
        _unused,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `/api/investor/BankTransactions`,
          params: { WealthAccountId: accountId, startDate, endDate },
        });
        return result.data ? { data: result.data } : { error: result.error };
      },
    }),
    getAccountPermissions: builder.query<unknown, string>({
      queryFn: async (accountId, _ctx, _unused, baseQuery) => {
        const result = await baseQuery(
          `/api/investor/Accounts/${accountId}/Permissions`,
        );
        return result.data ? { data: result.data } : { error: result.error };
      },
    }),
    getAccountDiversification: builder.query<unknown, string>({
      queryFn: async (accountId, _ctx, _unused, baseQuery) => {
        const result = await baseQuery(
          `/api/investor/AccountDiversification/${accountId}`,
        );
        return result.data ? { data: result.data } : { error: result.error };
      },
    }),
    getAccountNotes: builder.query<unknown, string>({
      queryFn: async (accountId, _ctx, _unused, baseQuery) => {
        const result = await baseQuery(
          `/api/investor/Accounts/${accountId}/Notes`,
        );
        return result.data ? { data: result.data } : { error: result.error };
      },
    }),
    getAccountTags: builder.query<AccountGroupCategory[], QueryNoArgs>({
      queryFn: async (_arg, _queryApi, _unused, baseQuery) => {
        const result = await baseQuery('/api/investor/AccountTags');
        if (result.error) {
          return { error: result.error };
        }
        // Manually apply the transformation from the original transformResponse
        const transformedData = (
          result.data as { Records: AccountGroupCategory[] }
        ).Records;
        return { data: transformedData };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ TagCategoryId }) => ({
                type: 'AccountTags' as const,
                id: TagCategoryId,
              })),
              { type: 'AccountTags', id: 'LIST' },
            ]
          : [{ type: 'AccountTags', id: 'LIST' }],
    }),
    // Tags filtered by specific wealth account (feed-tag assignment; AG-23-owned)
    getTagsForAccount: builder.query<AccountGroupCategory[], number>({
      queryFn: async (wealthAccountId, _queryApi, _unused, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/tags`,
          params: { wealthAccountId },
          method: 'GET',
        });
        return result.error
          ? { error: result.error }
          : {
              data: (result.data as { Records: AccountGroupCategory[] })
                .Records,
            };
      },
      providesTags: (result, _error, id) =>
        result
          ? [{ type: 'AccountTags', id }]
          : [{ type: 'AccountTags', id: 'LIST' }],
    }),
    getAllAccountTags: builder.query<AccountTag[], string>({
      queryFn: async (accountId, _queryApi, _unused, baseQuery) => {
        const result = await baseQuery(
          `/api/investor/Accounts/${accountId}/tags`,
        );
        return result.error
          ? { error: result.error }
          : { data: normalizeAccountTagsResponse(result.data) };
      },
      providesTags: ['AccountTags'],
    }),
    getAccountGroupCategories: builder.query<
      { Records: AccountGroupCategory[] },
      QueryNoArgs
    >({
      queryFn: async (_, { getState }, _unused, baseQuery) => {
        const currentRole = (getState() as WARootState).auth.currentRole;
        const result = await baseQuery({
          url: roleApiPath(currentRole, '/AccountTags'),
        });
        return result.error
          ? { error: result.error }
          : { data: result.data as { Records: AccountGroupCategory[] } };
      },
      providesTags: ['AccountGroupCategories'],
    }),
    createAccountGroup: builder.mutation<
      AccountGroupCategory,
      { name: string; description: string; options: string[] }
    >({
      queryFn: async (
        { name, description, options },
        _queryApi,
        _unused,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `/api/investor/AccountTags`,
          method: 'POST',
          body: {
            Tags: options
              .filter((option) => option)
              .map((option) => ({ Name: option })),
            Name: name,
            Description: description,
          },
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as AccountGroupCategory };
      },
      invalidatesTags: [
        { type: 'AccountTags', id: 'LIST' },
        'AccountGroupCategories',
      ],
    }),
    updateAccountGroup: builder.mutation<
      AccountGroupCategory,
      Partial<Omit<AccountGroupCategory, 'Tags'>> &
        Pick<AccountGroupCategory, 'TagCategoryId'> & {
          Tags: { Name: string; InvestorTagId?: number }[];
        }
    >({
      queryFn: async (data, _queryApi, _unused, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/AccountTags/${String(data.TagCategoryId)}`,
          method: 'PUT',
          body: data,
        });
        return result.error
          ? { error: result.error }
          : { data: result.data as AccountGroupCategory };
      },
      invalidatesTags: (_result, _error, { TagCategoryId }) => [
        { type: 'AccountTags', id: 'LIST' },
        { type: 'AccountTags', id: TagCategoryId },
        'AccountGroupCategories',
      ],
    }),
    deleteAccountGroup: builder.mutation<unknown, number>({
      queryFn: async (id, _queryApi, _unused, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/AccountTags/${String(id)}`,
          method: 'DELETE',
        });
        return result.error ? { error: result.error } : { data: result.data };
      },
      invalidatesTags: ['AccountTags', 'Accounts', 'AccountGroupCategories'],
    }),
    getAccountGroupDefaults: builder.query<unknown, number>({
      queryFn: async (tagCategoryId, _queryApi, _unused, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/AccountTags/${String(tagCategoryId)}/Defaults`,
          params: {
            'sort[0].Property': 'ClassificationName',
            'sort[0].Direction': 'asc',
            'sort[0].Order': 0,
            _: new Date().getTime(),
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data };
      },
    }),
    updateAccountGroupDefault: builder.mutation<
      unknown,
      { classificationId: number; tagId: number | null }
    >({
      queryFn: async (
        { classificationId, tagId },
        _queryApi,
        _unused,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `Investor/AccountTags/Defaults`,
          method: 'POST',
          body: { classificationId, tagId },
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
      invalidatesTags: ['AccountGroupDefaults'],
    }),
    updateAccountTags: builder.mutation<unknown, AccountBase>({
      queryFn: async (accountData, _queryApi, _unused, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/Accounts/${String(accountData.AccountId)}`,
          method: 'PUT',
          body: accountData,
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
      invalidatesTags: (_result, _error, { AccountId }) => [
        // Invalidate the specific account
        { type: 'Accounts', id: AccountId },
        // Invalidate the list of all accounts to refresh any view showing all accounts
        { type: 'Accounts', id: 'LIST' },
        // Invalidate account tags and groups as they may have changed
        'AccountTags',
        'AccountGroupCategories',
        // The following are based on the legacy UI's "affectedStores" logic.
        // These tags should be provided by other API slices (e.g., holdingsApi, investorsApi).
        // This ensures that any component displaying this data will re-fetch it.
        'Holdings',
        'Investors',
        'AccountsHistorical',
        'Portfolio', // A generic tag for portfolio-level summaries
        { type: 'Tags', id: 'LIST' },
        { type: 'Wealths', id: 'LIST' },
      ],
    }),
    reactivateAccount: builder.mutation<unknown, AccountBase>({
      queryFn: async (accountData, _queryApi, _unused, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/Accounts/${String(accountData.AccountId)}`,
          method: 'PUT',
          body: {
            ...accountData,
            ClosedDate: null,
          },
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
      invalidatesTags: (_result, _error, { AccountId }) => [
        { type: 'Accounts', id: AccountId },
        { type: 'Accounts', id: 'LIST' },
        'AccountTags',
        'AccountGroupCategories',
        'Holdings',
        'Investors',
        'AccountsHistorical',
        'Portfolio',
        { type: 'Tags', id: 'LIST' },
        { type: 'Wealths', id: 'LIST' },
      ],
    }),
    updateOfflineAccount: builder.mutation<unknown, OfflineAccount>({
      queryFn: async (accountData, _queryApi, _unused, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/offlineAccounts/${String(accountData.AccountId)}`,
          method: 'PUT',
          body: accountData,
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
      async onQueryStarted(accountData, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          accountsApi.util.updateQueryData(
            'getOfflineAccount',
            accountData.AccountId,
            (draft) => {
              Object.assign(draft, accountData);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: () => {
        const tags: (
          | 'AccountsHistorical'
          | 'AccountGroupCategories'
          | { type: 'Accounts'; id: 'LIST' }
          | { type: 'Tags'; id: 'LIST' }
          | { type: 'AccountTags'; id: 'LIST' }
          | { type: 'Holdings'; id: 'LIST' }
          | { type: 'Investors'; id: 'LIST' }
          | { type: 'Wealths'; id: 'LIST' }
          | { type: 'Portfolio'; id: 'LIST' }
        )[] = [
          { type: 'Accounts', id: 'LIST' },
          { type: 'Tags', id: 'LIST' },
          { type: 'AccountTags', id: 'LIST' },
          'AccountGroupCategories',
          'AccountsHistorical',
          { type: 'Holdings', id: 'LIST' },
          { type: 'Investors', id: 'LIST' },
          { type: 'Wealths', id: 'LIST' },
          { type: 'Portfolio', id: 'LIST' },
        ];

        return tags;
      },
    }),
    createOfflineAccount: builder.mutation<
      OfflineAccount,
      OfflineCreationPayload
    >({
      queryFn: async (payload, _ctx, _unused, baseQuery) => {
        const result = await baseQuery({
          url: '/api/investor/offlineAccounts',
          method: 'POST',
          body: payload,
        });
        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as OfflineAccount };
      },
      invalidatesTags: [
        'Accounts',
        'Tags',
        'Wealths',
        'Portfolio',
        'AccountTags',
        'AccountGroupCategories',
        'Investors',
      ],
    }),
    getOfflineAccount: builder.query<OfflineAccount, number>({
      queryFn: async (accountId, _queryApi, _unused, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/offlineAccounts/${String(accountId)}`,
          method: 'GET',
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as OfflineAccount };
      },
      providesTags: (_result, _error, id) => [{ type: 'Accounts', id }],
    }),
    deleteAccount: builder.mutation<unknown, number>({
      queryFn: async (accountId, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/Accounts/${String(accountId)}`,
          method: 'DELETE',
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
      invalidatesTags: (_result, _error, _accountId) => [
        { type: 'Accounts', id: 'LIST' },
        'Wealths',
        'Tags',
      ],
    }),
  }),
});

export const {
  useGetAccountsQuery: useGetAccountsQueryBase,
  useLazyGetAccountsQuery: useLazyGetAccountsQueryBase,
  useLazyGetAdvisorAccountQuery,
  useLazyGetAdvisorInvestorGroupsQuery,
  useGetAdvisorInvestorGroupByIdQuery,
  useGetAccountsHistoricalQuery: useGetAccountsHistoricalQueryBase,
  useLazyGetAccountsHistoricalQuery,
  useGetAccountHistoricalComparisonQuery:
    useGetAccountHistoricalComparisonQueryBase,
  useGetAccountHistoricalTrendDetailsQuery,
  useLazyGetAccountHistoricalTrendDetailsQuery,
  useGetAccountMarketPriceQuery: useGetAccountMarketPriceQueryBase,
  useGetVaultFileAccountsQuery: useGetVaultFileAccountsQueryBase,
  useGetAccountHistoricalTrendQuery,
  useGetSingleAccountHistoricalComparisonQuery,
  useGetAccountUnrealizedGainsQuery,
  useGetAccountRealizedGainsQuery,
  useGetAccountRealizedGainsSummaryQuery,
  useGetAccountDocumentsQuery,
  useGetAccountTransactionsQuery,
  useGetAccountPermissionsQuery,
  useGetAccountDiversificationQuery,
  useGetAccountNotesQuery,
  useGetAccountTagsQuery,
  useGetAllAccountTagsQuery,
  useGetAccountGroupCategoriesQuery,
  useGetAccountBarChartsQuery,
  useCreateAccountGroupMutation,
  useUpdateAccountGroupMutation,
  useDeleteAccountGroupMutation,
  useGetAccountGroupDefaultsQuery,
  useUpdateAccountGroupDefaultMutation,
  useUpdateAccountTagsMutation,
  useReactivateAccountMutation,
  useUploadAccountHistoryMutation,
  useUpdateOfflineAccountMutation,
  useDeleteAccountMutation,
  useGetOfflineAccountQuery,
  useGetTagsForAccountQuery,
  useCreateOfflineAccountMutation,
} = accountsApi;

type GetAccountsUseQueryOptions = TypedUseQuerySubscriptionOptions;

type GetAccountsUseQueryResult = TypedUseQueryHookResult<
  AccountViewModel,
  GetAccountsQueryArg,
  typeof waServicesQuery
>;

type GetAccountMarketPriceUseQueryOptions = Parameters<
  typeof accountsApi.endpoints.getAccountMarketPrice.useQuery
>[1];

type GetVaultFileAccountsUseQueryOptions = Parameters<
  typeof accountsApi.endpoints.getVaultFileAccounts.useQuery
>[1];

/**
 * Same as the RTK `getAccounts` subscription, but automatically merges the
 * source-aware `viewingTag.selectedViewingTag` selection. Firm tags use
 * `InvestorTagId`; investor-created tags use `TagId`. Explicit tag filters and
 * advisor-scoped `InvestorId` requests remain untouched.
 */
export function useGetAccountsQuery(
  arg: GetAccountsQueryArg | typeof skipToken,
  options?: GetAccountsUseQueryOptions,
): GetAccountsUseQueryResult {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : mergeViewingInvestorTagIntoGetAccountsArg(arg, confirmed.selection);

  return accountsApi.useGetAccountsQuery(queryArg, options);
}

type GetAccountHistoricalComparisonUseQueryOptions =
  TypedUseQuerySubscriptionOptions;

type GetAccountHistoricalComparisonUseQueryResult = TypedUseQueryHookResult<
  AccountHistoricalComparisonResponse | null,
  AccountHistoricalComparisonQueryArg,
  typeof waServicesQuery
>;

/**
 * Same as the RTK `getAccountHistoricalComparison` subscription, but
 * automatically merges the source-aware `viewingTag.selectedViewingTag`
 * selection. Firm and personal tags both use `InvestorTagId` (System A).
 * Explicit tag filters and advisor-scoped `InvestorId` requests remain untouched.
 */
export function useGetAccountHistoricalComparisonQuery(
  arg: AccountHistoricalComparisonQueryArg | typeof skipToken,
  options?: GetAccountHistoricalComparisonUseQueryOptions,
): GetAccountHistoricalComparisonUseQueryResult {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : mergeViewingInvestorTagIntoAccountHistoricalComparisonArg(
          arg,
          confirmed.selection,
        );

  return accountsApi.endpoints.getAccountHistoricalComparison.useQuery(
    queryArg,
    options,
  );
}

export type AccountsHistoricalQueryArg = Exclude<
  Parameters<typeof useGetAccountsHistoricalQueryBase>[0],
  typeof skipToken
>;

type GetAccountsHistoricalUseQueryResult = TypedUseQueryHookResult<
  AccountHistoricalTrendResponse | null,
  AccountsHistoricalQueryArg | typeof skipToken,
  typeof waServicesQuery
>;

export function useGetAccountsHistoricalQuery(
  arg: AccountsHistoricalQueryArg | typeof skipToken,
  options?: TypedUseQuerySubscriptionOptions,
): GetAccountsHistoricalUseQueryResult {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : mergeViewingTagIntoArg(arg, confirmed.selection);

  return useGetAccountsHistoricalQueryBase(queryArg, options);
}

export function useGetAccountMarketPriceQuery(
  arg: GetAccountMarketPriceQueryArg | typeof skipToken,
  options?: GetAccountMarketPriceUseQueryOptions,
) {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildMarketPriceSubscriptionArg(arg, confirmed.selection);

  return accountsApi.endpoints.getAccountMarketPrice.useQuery(
    queryArg,
    options,
  );
}

export function useGetVaultFileAccountsQuery(
  arg: GetVaultFileAccountsQueryArg | typeof skipToken,
  options?: GetVaultFileAccountsUseQueryOptions,
): ReturnType<typeof useGetVaultFileAccountsQueryBase> {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildVaultFileAccountsSubscriptionArg(arg, confirmed.selection);

  return accountsApi.endpoints.getVaultFileAccounts.useQuery(queryArg, options);
}

export function useLazyGetAccountsQuery(): ReturnType<
  typeof accountsApi.endpoints.getAccounts.useLazyQuery
> {
  const confirmed = useSelector(selectQueryViewingTag);
  const [trigger, ...rest] = accountsApi.endpoints.getAccounts.useLazyQuery();

  const mergedTrigger = useCallback(
    async (arg: GetAccountsQueryArg) =>
      trigger(
        mergeViewingInvestorTagIntoGetAccountsArg(
          arg,
          confirmed.ready ? confirmed.selection : null,
        ),
      ),
    [trigger, confirmed],
  );

  return [mergedTrigger, ...rest] as unknown as ReturnType<
    typeof accountsApi.endpoints.getAccounts.useLazyQuery
  >;
}
